import {
  BLOOM_INTENSITY,
  BLOOM_RADII,
  CORE_INTENSITY,
  HALO_INTENSITY,
  HDR_SCALE,
  hexToRgb,
  lightColor,
  mixHex,
  PAIR_WASH_FACTOR,
  type Pt,
  type Scene,
  type SceneRun,
  thrownColor,
  WALL_OFFSET,
  WASH_INTENSITY,
} from '@better-lights/core';
import type { DepthMap } from './types.ts';

/**
 * CPU side of the demo renderer: per-LED instance data for the WASH / EMITTER kernels and
 * stroke geometry (docs/spec/03 §5–§7). Pure, so it is unit-tested without a GPU.
 */

export interface WashParams {
  d0: number;
  reach: number;
  /** Quad in LED-local coords: x, y, w, h. */
  quad: [number, number, number, number];
}

/** §5.4: d0 = max(2, L·WALL_OFFSET), reach = 1.35 L, quad around the LED. */
export function washParams(scene: Scene): WashParams {
  const L = scene.washLength;
  const d0 = Math.max(2, L * WALL_OFFSET);
  const reach = L * 1.35;
  return { d0, reach, quad: [-reach * 0.85, -d0 * 1.4, reach * 1.7, reach + d0 * 1.4] };
}

/** Per-LED wash intensity (before the light color): WASH_INTENSITY × pair factor × gain × HDR_SCALE. */
export function washPerLed(scene: Scene, gain: number): number {
  return WASH_INTENSITY * (scene.style.layout === 'pair' ? PAIR_WASH_FACTOR : 1) * gain * HDR_SCALE;
}

export const WASH_STRIDE = 5;

/** [x, y, r, g, b] per LED with `thrownColor(color, perLed)` (cached per color). */
export function washInstances(runs: SceneRun[], perLed: number): Float32Array {
  const n = runs.reduce((a, r) => a + r.lights.length, 0);
  const out = new Float32Array(n * WASH_STRIDE);
  const cache = new Map<string, [number, number, number]>();
  let o = 0;
  for (const run of runs) {
    for (const led of run.lights) {
      let c = cache.get(led.color);
      if (!c) {
        c = thrownColor(led.color, perLed);
        cache.set(led.color, c);
      }
      out[o++] = led.x;
      out[o++] = led.y;
      out[o++] = c[0];
      out[o++] = c[1];
      out[o++] = c[2];
    }
  }
  return out;
}

/** x, y, r, g, b, rc, core, halo, bloom, bloomR, extent. */
export const EMITTER_STRIDE = 11;

/** §5.6 emitters: core + halo (+ bloom) kernels, with the pair rules for shared / own glows. */
export function emitterInstances(scene: Scene, gain: number): Float32Array {
  const rc = scene.emitterRadius;
  const bloomR = scene.coreRadius * BLOOM_RADII;
  const extent = bloomR * 2.6;
  const pointExt = rc * 8;
  const k = gain * HDR_SCALE;
  const out: number[] = [];
  const cache = new Map<string, [number, number, number]>();
  const color = (hex: string) => {
    let c = cache.get(hex);
    if (!c) {
      c = lightColor(hex, 1);
      cache.set(hex, c);
    }
    return c;
  };
  const push = (p: Pt, hex: string, core: number, halo: number, bloom: number, ext: number) => {
    const c = color(hex);
    out.push(p.x, p.y, c[0], c[1], c[2], rc, core * k, halo * k, bloom * k, bloomR, ext);
  };
  const pairs = scene.style.layout === 'pair';
  for (const run of scene.runs) {
    const lights = run.lights;
    for (let i = 0; i < lights.length; i++) {
      const led = lights[i];
      if (!pairs) {
        push(led, led.color, CORE_INTENSITY, HALO_INTENSITY, BLOOM_INTENSITY, extent);
        continue;
      }
      push(led, led.color, CORE_INTENSITY, HALO_INTENSITY, 0, pointExt);
      const mate = led.member === 0 ? lights[i + 1] : lights[i - 1];
      const paired = mate !== undefined && mate.group === led.group;
      if (paired && mate.color === led.color) {
        if (led.member === 0) {
          push({ x: (led.x + mate.x) / 2, y: (led.y + mate.y) / 2 }, led.color, 0, 0, BLOOM_INTENSITY * 1.6, extent);
        }
      } else {
        push(led, led.color, 0, 0, BLOOM_INTENSITY * 0.8, extent);
      }
    }
  }
  return Float32Array.from(out);
}

/** ax, ay, bx, by, buttStart, buttEnd. */
export const SEGMENT_STRIDE = 6;

/**
 * Segments of a polyline stroke for the coverage pass. Round joins come from the union of
 * capsules; `butt` makes the two ends of the whole path square (track: Butt cap, Round join).
 */
export function segmentInstances(pts: Pt[], butt: boolean): Float32Array {
  if (pts.length < 2) return new Float32Array(0);
  const n = pts.length - 1;
  const out = new Float32Array(n * SEGMENT_STRIDE);
  for (let i = 0; i < n; i++) {
    const o = i * SEGMENT_STRIDE;
    out[o] = pts[i].x;
    out[o + 1] = pts[i].y;
    out[o + 2] = pts[i + 1].x;
    out[o + 3] = pts[i + 1].y;
    out[o + 4] = butt && i === 0 ? 1 : 0;
    out[o + 5] = butt && i === n - 1 ? 1 : 0;
  }
  return out;
}

/**
 * §5.4 `runDepthOf`: upper median of the inverse depth just below a run's LEDs
 * (y + 0.15 · washLength). No lights → 0.5.
 */
export function runDepthOf(depth: DepthMap, scene: Scene, run: SceneRun): number {
  const { width: w, height: h, data } = depth;
  const samples: number[] = [];
  for (const led of run.lights) {
    const x = Math.max(0, Math.min(w - 1, Math.round((led.x / scene.width) * (w - 1))));
    const y = Math.max(0, Math.min(h - 1, Math.round(((led.y + scene.washLength * 0.15) / scene.height) * (h - 1))));
    samples.push(data[y * w + x] / 255);
  }
  if (samples.length === 0) return 0.5;
  samples.sort((a, b) => a - b);
  return samples[Math.floor(samples.length / 2)];
}

/** Linear premultiplied color × HDR_SCALE (`premul` in §5.5). */
export function premulHdr(linear: [number, number, number], a: number): [number, number, number, number] {
  return [linear[0] * HDR_SCALE * a, linear[1] * HDR_SCALE * a, linear[2] * HDR_SCALE * a, a];
}

/** sRGB OETF used by the resolve pass: out = OETF(clamp(16 v, 0, 1)). */
export function resolveChannel(v: number): number {
  const x = Math.max(0, Math.min(1, v * 16));
  return x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
}

/* -------------------------------------------------------------------- LDR fallback */

/** x, y, r, g, b (sRGB 0..1) per LED for the LDR conical wash. */
export function ldrWashInstances(runs: SceneRun[]): Float32Array {
  const out: number[] = [];
  for (const run of runs) for (const led of run.lights) out.push(led.x, led.y, ...srgb01(led.color));
  return Float32Array.from(out);
}

/** x, y, kind (0 bloom, 1 core), radius, glow, c.rgb, mix72.rgb, mix25.rgb. */
export const LDR_EMITTER_STRIDE = 14;

/** §6 drawRunEmittersLdr: per LED a bloom circle then a core circle (SrcOver inside a Plus layer). */
export function ldrEmitterInstances(scene: Scene, night: number, brightness: number): Float32Array {
  const r = scene.emitterRadius;
  const bloomR = r * (5 + 9 * night) * Math.sqrt(brightness);
  const glow = (0.25 + 0.75 * night) * Math.min(1.4, brightness);
  const out: number[] = [];
  for (const run of scene.runs) {
    for (const led of run.lights) {
      const c = srgb01(led.color);
      const m72 = srgb01(mixHex(led.color, '#FFFFFF', 0.72));
      const m25 = srgb01(mixHex(led.color, '#FFFFFF', 0.25));
      out.push(led.x, led.y, 0, bloomR, glow, ...c, ...m72, ...m25);
      out.push(led.x, led.y, 1, r * 1.7, glow, ...c, ...m72, ...m25);
    }
  }
  return Float32Array.from(out);
}

export function srgb01(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex);
  return [r / 255, g / 255, b / 255];
}
